<template>
    <ChatLayout>
        <div class="h-full overflow-y-auto bg-slate-50">
            <article class="mx-auto max-w-4xl px-4 py-10 text-slate-800 sm:px-6 lg:px-8">
                <Link href="/account/api" class="text-sm font-semibold text-primary-700">Back to API keys</Link>
                <h1 class="mt-4 text-3xl font-bold text-slate-950">Clipfinder API v1</h1>
                <p class="mt-3 text-slate-600">
                    Run the same asynchronous search flow as Clipfinder chat and retrieve structured quotes, timestamps, playback links and podcast
                    metadata.
                </p>

                <section class="mt-10" aria-labelledby="authentication-heading">
                    <h2 id="authentication-heading" class="text-xl font-semibold text-slate-950">Authentication and limits</h2>
                    <p class="mt-2 leading-7 text-slate-700">
                        Send the one-time key as <code>Authorization: Bearer &lt;key&gt;</code>. Browser cookies and wildcard abilities are rejected.
                        New searches consume the account's shared daily quota. Polling, listing podcasters, next-batch calls and idempotent replays do
                        not. An ample pre-authentication IP limit and a separate post-authentication per-user burst limit protect v1.
                    </p>
                </section>

                <section class="mt-10" aria-labelledby="mcp-heading">
                    <h2 id="mcp-heading" class="text-xl font-semibold text-slate-950">Remote MCP</h2>
                    <p class="mt-2 leading-7 text-slate-700">
                        Use the same API key with the stateless Streamable HTTP endpoint
                        <code>https://api.clipfinder.com/mcp/clipfinder</code>. It supports Workbench, Claude, Codex and other MCP clients. Session
                        resume is not supported and an MCP session id never grants authentication, ownership or idempotency.
                    </p>
                    <p class="mt-2 leading-7 text-slate-700">
                        Start once with a required <code>idempotency_key</code>, then poll <code>clipfinder_get_search</code> while
                        <code>poll.required</code> is true. Use a required <code>operation_key</code> for durable screen-next retries.
                    </p>
                    <div v-for="example in mcpConfigurations" :key="example.title" class="mt-5">
                        <h3 class="font-semibold text-slate-900">{{ example.title }}</h3>
                        <pre
                            class="mt-2 overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs leading-6 text-slate-100"
                        ><code>{{ example.code }}</code></pre>
                    </div>
                    <p class="mt-4 text-sm text-slate-700">
                        Keep <code>CLIPFINDER_API_KEY</code> in an environment variable or client secret manager. Never commit the plaintext key.
                    </p>
                </section>

                <section v-for="section in mcpSections" :key="section.title" class="mt-10" :aria-labelledby="section.id">
                    <h2 :id="section.id" class="text-xl font-semibold text-slate-950">{{ section.title }}</h2>
                    <p class="mt-2 whitespace-pre-line leading-7 text-slate-700">{{ section.description }}</p>
                    <h3 class="mt-4 font-semibold text-slate-900">JSON-RPC request</h3>
                    <pre
                        class="mt-2 overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs leading-6 text-slate-100"
                    ><code>{{ section.request }}</code></pre>
                    <h3 class="mt-4 font-semibold text-slate-900">Structured response</h3>
                    <pre
                        class="mt-2 overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs leading-6 text-slate-100"
                    ><code>{{ section.response }}</code></pre>
                </section>

                <section class="mt-10" aria-labelledby="mcp-error-codes-heading">
                    <h2 id="mcp-error-codes-heading" class="text-xl font-semibold text-slate-950">MCP tool error codes</h2>
                    <p class="mt-2 text-slate-700">Expected tool failures keep HTTP 200, set <code>isError</code> to true and use this safe shape.</p>
                    <dl class="mt-4 grid gap-2 text-sm sm:grid-cols-[auto_1fr]">
                        <template v-for="error in mcpErrorCodes" :key="error[0]">
                            <dt class="font-mono font-semibold text-slate-950">{{ error[0] }}</dt>
                            <dd class="text-slate-700">{{ error[1] }}</dd>
                        </template>
                    </dl>
                </section>

                <section v-for="section in sections" :key="section.title" class="mt-10" :aria-labelledby="section.id">
                    <h2 :id="section.id" class="text-xl font-semibold text-slate-950">{{ section.title }}</h2>
                    <p class="mt-2 whitespace-pre-line leading-7 text-slate-700">{{ section.description }}</p>
                    <h3 class="mt-4 font-semibold text-slate-900">Request</h3>
                    <pre
                        class="mt-2 overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs leading-6 text-slate-100"
                    ><code>{{ section.request }}</code></pre>
                    <h3 class="mt-4 font-semibold text-slate-900">Response</h3>
                    <pre
                        class="mt-2 overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs leading-6 text-slate-100"
                    ><code>{{ section.response }}</code></pre>
                </section>

                <section class="mt-10" aria-labelledby="errors-heading">
                    <h2 id="errors-heading" class="text-xl font-semibold text-slate-950">Errors</h2>
                    <p class="mt-2 text-slate-700">
                        Errors intentionally returned by the documented v1 middleware and endpoints are JSON with an error code and safe message.
                        Validation errors also contain field details.
                    </p>
                    <h3 class="mt-4 font-semibold text-slate-900">Example invalid request</h3>
                    <pre
                        class="mt-2 overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs leading-6 text-slate-100"
                    ><code>{{ errorRequest }}</code></pre>
                    <dl class="mt-4 grid gap-2 text-sm sm:grid-cols-[auto_1fr]">
                        <template v-for="error in errors" :key="error.code">
                            <dt class="font-mono font-semibold text-slate-950">{{ error.status }} {{ error.code }}</dt>
                            <dd class="text-slate-700">
                                <p>{{ error.description }}</p>
                                <pre
                                    class="mt-2 overflow-x-auto rounded bg-slate-900 p-3 text-xs text-slate-100"
                                ><code>{{ error.example }}</code></pre>
                            </dd>
                        </template>
                    </dl>
                </section>
            </article>
        </div>
    </ChatLayout>
</template>

<script setup lang="ts">
import ChatLayout from '@/Layouts/ChatLayout.vue'
import { defineUserStore } from '@/Stores/user'
import { Link } from '@inertiajs/vue3'

defineUserStore()

const mcpConfigurations = [
    {
        title: 'Workbench or Claude .mcp.json',
        code: `{
  "mcpServers": {
    "clipfinder": {
      "type": "http",
      "url": "https://api.clipfinder.com/mcp/clipfinder",
      "headers": { "Authorization": "Bearer \${CLIPFINDER_API_KEY}" }
    }
  }
}`,
    },
    {
        title: 'Codex config.toml',
        code: `[mcp_servers.clipfinder]
url = "https://api.clipfinder.com/mcp/clipfinder"
bearer_token_env_var = "CLIPFINDER_API_KEY"`,
    },
    {
        title: 'Generic protocol smoke test',
        code: `curl https://api.clipfinder.com/mcp/clipfinder \\
  -H "Authorization: Bearer $CLIPFINDER_API_KEY" \\
  -H "Accept: application/json, text/event-stream" \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"ping","params":{}}'`,
    },
]

const mcpSearchData = `{
  "ok": true,
  "data": {
    "id": "SEARCH_UUID",
    "conversation_id": "CONVERSATION_UUID",
    "status": "queued",
    "question": { "original": "How can I sleep better?", "enhanced": null },
    "period": { "from_year": 2022, "till_year": 2026 },
    "participants": [{ "id": "PODCASTER_UUID", "slug": "health-channel", "name": "Health Channel" }],
    "results": [],
    "suggestions": [],
    "has_more": false,
    "created_at": "2026-08-16T10:00:00+02:00",
    "completed_at": null,
    "links": {
      "self": "https://api.clipfinder.com/api/v1/clipfinder/searches/SEARCH_UUID",
      "conversation": "https://clipfinder.com/conversations/CONVERSATION_UUID",
      "next": "https://api.clipfinder.com/api/v1/clipfinder/searches/SEARCH_UUID/next"
    }
  },
  "poll": { "required": true, "after_seconds": 3, "tool": "clipfinder_get_search" },
  "quota": { "limit": 100, "used": 1, "remaining": 99, "reset_at": 1786917600 },
  "idempotent_replayed": false,
  "error": null
}`

const mcpSections = [
    {
        id: 'mcp-list-podcasters',
        title: 'MCP clipfinder_list_podcasters',
        description: 'Lists the exact searchable scope. Listing does not consume daily search quota.',
        request: '{"jsonrpc":"2.0","id":1,"method":"tools/call","params":{"name":"clipfinder_list_podcasters","arguments":{"search":"health"}}}',
        response:
            '{\n  "ok": true,\n  "data": [{ "id": "PODCASTER_UUID", "slug": "health-channel", "name": "Health Channel", "podcasts_count": 84 }],\n  "poll": null,\n  "quota": { "limit": 100, "used": 0, "remaining": 100, "reset_at": 1786917600 },\n  "idempotent_replayed": false,\n  "error": null\n}',
    },
    {
        id: 'mcp-start-search',
        title: 'MCP clipfinder_start_search',
        description: 'Starts durable asynchronous work. The idempotency key is required and an identical replay never consumes quota twice.',
        request:
            '{"jsonrpc":"2.0","id":2,"method":"tools/call","params":{"name":"clipfinder_start_search","arguments":{"question":"How can I sleep better?","from_year":2022,"till_year":2026,"participant_ids":["PODCASTER_UUID"],"idempotency_key":"search-123"}}}',
        response: mcpSearchData,
    },
    {
        id: 'mcp-get-search',
        title: 'MCP clipfinder_get_search',
        description:
            'Polls an owned search without consuming quota. Completed data contains selected quotes, timestamps, playback URLs and podcast metadata exactly as in the REST response below.',
        request: '{"jsonrpc":"2.0","id":3,"method":"tools/call","params":{"name":"clipfinder_get_search","arguments":{"search_id":"SEARCH_UUID"}}}',
        response:
            '{\n  "ok": true,\n  "data": { "id": "SEARCH_UUID", "status": "completed", "results": [{ "quote": "Consistency matters.", "timestamp_seconds": 642, "playback_timestamp_seconds": 637, "playback_url": "https://www.youtube.com/watch?v=video-123&t=637s", "score": 0.94, "caption": null, "podcast": { "id": "PODCAST_UUID", "title": "Practical sleep habits", "published_at": "2026-04-10T00:00:00+02:00", "type": "youtube", "external_id": "video-123", "source_url": "https://www.youtube.com/watch?v=video-123", "guests": [] } }] },\n  "poll": { "required": false, "after_seconds": null, "tool": null },\n  "quota": { "limit": 100, "used": 1, "remaining": 99, "reset_at": 1786917600 },\n  "idempotent_replayed": false,\n  "error": null\n}',
    },
    {
        id: 'mcp-screen-next',
        title: 'MCP clipfinder_screen_next',
        description:
            'Screens one next batch without consuming quota. The response shown is a schematic structuredContent example for a newly accepted operation. A replay with the same operation key sets idempotent_replayed to true.',
        request:
            '{"jsonrpc":"2.0","id":4,"method":"tools/call","params":{"name":"clipfinder_screen_next","arguments":{"search_id":"SEARCH_UUID","operation_key":"next-123"}}}',
        response: mcpSearchData.replace('"status": "queued"', '"status": "processing"'),
    },
    {
        id: 'mcp-errors',
        title: 'MCP tool error',
        description:
            'Expected business errors set isError to true and contain safe text plus the same structured error. Exception details are never returned.',
        request: '{"jsonrpc":"2.0","id":5,"method":"tools/call","params":{"name":"clipfinder_get_search","arguments":{"search_id":"MISSING_UUID"}}}',
        response:
            '{\n  "content": [{ "type": "text", "text": "{\\"ok\\":false,\\"data\\":null,\\"poll\\":null,\\"quota\\":null,\\"idempotent_replayed\\":false,\\"error\\":{\\"code\\":\\"search_not_found\\",\\"message\\":\\"The requested search was not found.\\"}}" }],\n  "structuredContent": { "ok": false, "data": null, "poll": null, "quota": null, "idempotent_replayed": false, "error": { "code": "search_not_found", "message": "The requested search was not found." } },\n  "isError": true\n}',
    },
]

const mcpErrorCodes = [
    ['validation_failed', 'Tool arguments or podcasters are invalid.'],
    ['daily_quota_exceeded', 'The shared daily new-search quota is exhausted.'],
    ['idempotency_key_conflict', 'The start key was used with another payload.'],
    ['search_not_found', 'The search is missing or belongs to another user.'],
    ['search_failed', 'A failed search cannot screen another batch.'],
    ['screening_in_progress', 'Another operation key owns the active next batch.'],
    ['screening_unavailable', 'The durable next run could not be persisted.'],
    ['search_unavailable', 'The durable initial search could not be persisted.'],
    ['operation_unavailable', 'An unexpected failure was converted to a safe retryable response.'],
]

const queuedSearch = `HTTP/1.1 202 Accepted
Location: https://api.clipfinder.com/api/v1/clipfinder/searches/SEARCH_UUID
Retry-After: 3
X-Clipfinder-RateLimit-Limit: 100
X-Clipfinder-RateLimit-Remaining: 99
X-Clipfinder-RateLimit-Reset: 1786917600

{
  "data": {
    "id": "SEARCH_UUID",
    "conversation_id": "CONVERSATION_UUID",
    "status": "queued",
    "question": { "original": "How can I sleep better?", "enhanced": null },
    "period": { "from_year": 2022, "till_year": 2026 },
    "participants": [{ "id": "PODCASTER_UUID", "slug": "health-channel", "name": "Health Channel" }],
    "results": [],
    "suggestions": [],
    "has_more": false,
    "created_at": "2026-08-16T10:00:00+02:00",
    "completed_at": null,
    "links": {
      "self": "https://api.clipfinder.com/api/v1/clipfinder/searches/SEARCH_UUID",
      "conversation": "https://clipfinder.com/conversations/CONVERSATION_UUID",
      "next": "https://api.clipfinder.com/api/v1/clipfinder/searches/SEARCH_UUID/next"
    }
  }
}`

const completedSearch = `HTTP/1.1 200 OK

{
  "data": {
    "id": "SEARCH_UUID",
    "conversation_id": "CONVERSATION_UUID",
    "status": "completed",
    "question": { "original": "How can I sleep better?", "enhanced": "Which habits improve sleep quality?" },
    "period": { "from_year": 2022, "till_year": 2026 },
    "participants": [{ "id": "PODCASTER_UUID", "slug": "health-channel", "name": "Health Channel" }],
    "results": [{
      "id": "CLIP_UUID",
      "status": "ready",
      "quote": "Consistency matters more than a perfect bedtime.",
      "timestamp_seconds": 642,
      "playback_timestamp_seconds": 637,
      "playback_url": "https://www.youtube.com/watch?v=video-123&t=637s",
      "score": 0.94,
      "caption": null,
      "podcast": {
        "id": "PODCAST_UUID",
        "title": "Practical sleep habits",
        "published_at": "2026-04-10T00:00:00+02:00",
        "type": "youtube",
        "external_id": "video-123",
        "source_url": "https://www.youtube.com/watch?v=video-123",
        "guests": []
      }
    }],
    "suggestions": ["Morning light", "Caffeine timing"],
    "has_more": true,
    "created_at": "2026-08-16T10:00:00+02:00",
    "completed_at": "2026-08-16T10:01:12+02:00",
    "links": {
      "self": "https://api.clipfinder.com/api/v1/clipfinder/searches/SEARCH_UUID",
      "conversation": "https://clipfinder.com/conversations/CONVERSATION_UUID",
      "next": "https://api.clipfinder.com/api/v1/clipfinder/searches/SEARCH_UUID/next"
    }
  }
}`

const processingSearch = completedSearch
    .replace('HTTP/1.1 200 OK', 'HTTP/1.1 202 Accepted\nLocation: https://api.clipfinder.com/api/v1/clipfinder/searches/SEARCH_UUID\nRetry-After: 3')
    .replace('"status": "completed"', '"status": "processing"')
    .replace('"completed_at": "2026-08-16T10:01:12+02:00"', '"completed_at": null')

const exhaustedSearch = completedSearch.replace('"has_more": true', '"has_more": false')

const errorRequest = `POST /api/v1/clipfinder/searches HTTP/1.1
Host: api.clipfinder.com
Authorization: Bearer 1|your-plaintext-key
Accept: application/json
Content-Type: application/json

{"question":"","from_year":2026,"till_year":2022,"podcasters":[]}`

const sections = [
    {
        id: 'podcasters',
        title: 'GET /api/v1/podcasters',
        description: 'Lists searchable podcasters. Add ?search=health to search by name or slug.',
        request:
            'GET /api/v1/podcasters?search=health HTTP/1.1\nHost: api.clipfinder.com\nAuthorization: Bearer 1|your-plaintext-key\nAccept: application/json',
        response:
            'HTTP/1.1 200 OK\n\n{\n  "data": [{\n    "id": "PODCASTER_UUID",\n    "slug": "health-channel",\n    "name": "Health Channel",\n    "podcasts_count": 84\n  }]\n}',
    },
    {
        id: 'start-search',
        title: 'POST /api/v1/clipfinder/searches',
        description:
            'Starts a durable asynchronous search. Use a unique Idempotency-Key so network retries return the same search without another quota charge. Follow Location and Retry-After from a 202 response.',
        request:
            'POST /api/v1/clipfinder/searches HTTP/1.1\nHost: api.clipfinder.com\nAuthorization: Bearer 1|your-plaintext-key\nAccept: application/json\nContent-Type: application/json\nIdempotency-Key: search-123\n\n{"question":"How can I sleep better?","from_year":2022,"till_year":2026,"podcasters":["PODCASTER_UUID"]}',
        response: queuedSearch,
    },
    {
        id: 'poll-search',
        title: 'GET /api/v1/clipfinder/searches/{search_id}',
        description:
            'Returns queued, processing, completed or failed. Results contain selected quotes only, sorted by score and timestamp. Internal traces, transcripts and rejected candidates are never exposed.',
        request:
            'GET /api/v1/clipfinder/searches/SEARCH_UUID HTTP/1.1\nHost: api.clipfinder.com\nAuthorization: Bearer 1|your-plaintext-key\nAccept: application/json',
        response: completedSearch,
    },
    {
        id: 'next-batch',
        title: 'POST /api/v1/clipfinder/searches/{search_id}/next',
        description:
            'Starts one next screening batch. Concurrent calls join the same active batch. A 202 response must be polled. A 200 response with has_more false means the result pool is exhausted.',
        request:
            'POST /api/v1/clipfinder/searches/SEARCH_UUID/next HTTP/1.1\nHost: api.clipfinder.com\nAuthorization: Bearer 1|your-plaintext-key\nAccept: application/json',
        response: `${processingSearch}\n\nWhen no candidates remain:\n\n${exhaustedSearch}`,
    },
]

const errors = [
    {
        status: 401,
        code: 'unauthenticated',
        description: 'The Bearer key is missing, invalid or revoked.',
        example: '{"error":{"code":"unauthenticated","message":"Provide a valid Clipfinder API key as a Bearer token."}}',
    },
    {
        status: 403,
        code: 'api_access_denied',
        description: 'The account or literal clipfinder:search ability is unavailable.',
        example: '{"error":{"code":"api_access_denied","message":"This account or API key does not have Clipfinder API access."}}',
    },
    {
        status: 404,
        code: 'search_not_found',
        description: 'The search does not exist or belongs to another user.',
        example: '{"error":{"code":"search_not_found","message":"The requested search was not found."}}',
    },
    {
        status: 409,
        code: 'idempotency_key_conflict',
        description: 'The idempotency key was used with another payload.',
        example: '{"error":{"code":"idempotency_key_conflict","message":"This Idempotency-Key was already used with a different request payload."}}',
    },
    {
        status: 409,
        code: 'search_failed',
        description: 'A failed search cannot load another batch.',
        example: '{"error":{"code":"search_failed","message":"The search is in a failed state and cannot load another batch."}}',
    },
    {
        status: 422,
        code: 'validation_failed',
        description: 'The request fields are invalid.',
        example:
            '{"error":{"code":"validation_failed","message":"The request payload is invalid.","details":{"fields":{"question":["The question field is required."]}}}}',
    },
    {
        status: 429,
        code: 'daily_quota_exceeded',
        description: 'The account daily search quota is exhausted.',
        example: '{"error":{"code":"daily_quota_exceeded","message":"The daily Clipfinder API search quota has been exhausted."}}',
    },
    {
        status: 429,
        code: 'burst_rate_exceeded',
        description: 'The pre-auth IP or post-auth user burst limit was exceeded.',
        example: '{"error":{"code":"burst_rate_exceeded","message":"Too many Clipfinder API requests. Please retry later."}}',
    },
    {
        status: 503,
        code: 'search_unavailable',
        description: 'The durable search could not be created.',
        example: '{"error":{"code":"search_unavailable","message":"The search could not be started. Please try again."}}',
    },
]
</script>
